import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { fetchFlexiblePageContent, type FlexiblePageContent } from "@/lib/homepage";
import { readPrerenderJson } from "@/lib/prerenderData";
import { extractPathSegments } from "@/lib/routing";

type PreviewInfo = {
  id: number;
  nonce: string;
  lang?: string;
} | null;

function markPrerenderReady() {
  if (typeof window !== "undefined") {
    (window as any).__PRERENDER_READY__ = true;
  }
}

function parsePreview(search: string): PreviewInfo {
  const params = new URLSearchParams(search);
  const isPreview = params.get("preview") === "true" || params.get("preview") === "1";
  const idParam = params.get("preview_id") ?? params.get("p");
  const nonce = params.get("preview_nonce") ?? params.get("_wpnonce");
  const lang = params.get("lang")?.trim().toLowerCase() || undefined;

  if (!isPreview && !idParam) return null;
  if (!idParam || !nonce) return null;

  const id = Number(idParam);
  if (!Number.isFinite(id) || id <= 0) return null;

  return { id, nonce, lang };
}

function normaliseSlugs(slugs: string[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];

  for (const raw of slugs) {
    if (!raw) continue;
    const trimmed = raw.trim().replace(/^\/+|\/+$/g, "").toLowerCase();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    ordered.push(trimmed);
  }

  return ordered;
}

export type UseFlexiblePageOptions = {
  slugs: string[];
  prerenderId: string;
  lang?: string;
};

export type UseFlexiblePageState = {
  content: FlexiblePageContent | null;
  loading: boolean;
  error: string | null;
  preview: PreviewInfo;
};

export function useFlexiblePageContent({
  slugs,
  prerenderId,
  lang,
}: UseFlexiblePageOptions): UseFlexiblePageState {
  const location = useLocation();
  const preview = useMemo(() => parsePreview(location.search), [location.search]);
  const slugList = useMemo(() => normaliseSlugs(slugs), [slugs]);
  const pathSegments = useMemo(() => {
    const segments = extractPathSegments(location.pathname);
    if (segments.length === 0) return segments;
    const first = segments[0];
    if (first === "fr" || first === lang) {
      return segments.slice(1);
    }
    return segments;
  }, [location.pathname, lang]);

  const inline = useMemo(() => {
    if (typeof window === "undefined" || preview) return null;
    return readPrerenderJson<FlexiblePageContent>(prerenderId);
  }, [preview, prerenderId]);

  const slugKey = useMemo(
    () =>
      [
        lang ?? "default",
        preview?.id ?? "none",
        preview?.nonce ?? "none",
        preview?.lang ?? "none",
        slugList.join("/"),
        pathSegments.join("/"),
      ].join("|"),
    [lang, preview?.id, preview?.nonce, preview?.lang, slugList, pathSegments],
  );

  const [content, setContent] = useState<FlexiblePageContent | null>(inline);
  const [loading, setLoading] = useState(!inline);
  const [error, setError] = useState<string | null>(null);
  const lastResolvedKeyRef = useRef<string | null>(inline ? slugKey : null);

  useEffect(() => {
    if (inline) {
      setContent(inline);
      setLoading(false);
      setError(null);
      lastResolvedKeyRef.current = slugKey;
      markPrerenderReady();
      return;
    }

    if (lastResolvedKeyRef.current !== slugKey) {
      setContent(null);
      setError(null);
      setLoading(true);
      lastResolvedKeyRef.current = null;
    }
  }, [inline, slugKey]);

  useEffect(() => {
    if (slugList.length === 0) {
      setContent(null);
      setError("Missing page slug");
      setLoading(false);
      lastResolvedKeyRef.current = null;
      markPrerenderReady();
      return;
    }

    if (inline) {
      return;
    }

    if (lastResolvedKeyRef.current === slugKey) {
      markPrerenderReady();
      return;
    }

    const preloaded = readPrerenderJson<FlexiblePageContent>(prerenderId);
    if (preloaded) {
      setContent(preloaded);
      setError(null);
      setLoading(false);
      lastResolvedKeyRef.current = slugKey;
      markPrerenderReady();
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchFlexiblePageContent(slugList, {
      lang,
      pathSegments,
      ...(preview
        ? {
            previewId: preview.id,
            previewNonce: preview.nonce,
            previewLang: preview.lang ?? lang,
          }
        : {}),
    })
      .then((data) => {
        if (cancelled) return;
        setContent(data);
        if (!data) {
          setError("Content not found");
        } else {
          setError(null);
        }
        lastResolvedKeyRef.current = slugKey;
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setContent(null);
        setError(err instanceof Error ? err.message : String(err));
        lastResolvedKeyRef.current = slugKey;
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          markPrerenderReady();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [inline, slugKey, slugList, preview, lang, pathSegments, prerenderId]);

  return { content, loading, error, preview };
}
